"use client";

import React from "react";
import { PhoneCall, ScanSearch, Wrench, ClipboardCheck, ShieldAlert, ArrowRight, Clock } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
interface ProcessStep {
  step: string;
  title: string;
  badge: string;
  description: string;
  details: string;
  icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
}
const steps: ProcessStep[] = [{
  step: "01",
  title: "Emergency Call & Dispatch",
  badge: "Immediate Response",
  description: "Your call connects straight to local supervisors in Maryland, DC, or Northern Virginia.",
  details: "We log incident details, triage active hazards, and roll our fully equipped mitigation truck immediately.",
  icon: PhoneCall
}, {
  step: "02",
  title: "On-Site Assessment",
  badge: "Precision Inspection",
  description: "Field technicians map water migration and heat patterns with calibrated thermal cameras.",
  details: "We isolate structural moisture pockets, photograph damage for your adjuster, and provide a clear scope.",
  icon: ScanSearch
}, {
  step: "03",
  title: "Mitigation & Restoration",
  badge: "Active Containment",
  description: "Industrial extractors and desiccant dehumidifiers run around the clock to stop secondary damage.",
  details: "We establish negative air zones, strip compromised materials, and disinfect framing to prevent mold.",
  icon: Wrench
}, {
  step: "04",
  title: "Final Walkthrough",
  badge: "Clearance & Sign-off",
  description: "Every drying chamber receives moisture clearance verification before we break down gear.",
  details: "We deliver full drying logs, thermal reports, and direct claim documentation for an orderly settlement.",
  icon: ClipboardCheck
}];
export default function Process() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.32,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="our-process" data-nav="light" className="relative bg-background py-16 md:py-24 border-b border-border overflow-hidden">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-12 border-b border-border">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold uppercase tracking-wider mb-4">
              <Clock className="size-3.5" strokeWidth={2} />
              <span>Standard Operational Workflow</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground font-default">
              What happens after you call
            </h2>
            <p className="mt-3 text-base md:text-lg text-muted-foreground font-sans leading-relaxed">
              From the initial dispatch to the final clearance check, here is how we restore your property. Local teams manage each step without call centers or delays.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <div className="flex items-center gap-2 px-3.5 py-2 rounded-md bg-muted border border-border text-xs font-mono text-muted-foreground">
              <span className="inline-block size-2 rounded-full bg-primary animate-pulse" />
              <span>Crews on standby across MD, DC &amp; NOVA</span>
            </div>
            <a href="tel:855-435-6631" className="inline-flex items-center gap-2 text-xs font-semibold text-primary hover:underline underline-offset-4">
              <span>Call 855-435-6631</span>
              <ArrowRight className="size-3.5" />
            </a>
          </div>
        </div>

        {/* Linear Step Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 pt-12">
          {steps.map((item, i) => {
          const IconComponent = item.icon;
          return <motion.div key={item.step} variants={itemVariants} data-index={i} className="relative flex flex-col h-full">
                <Card className="h-full flex flex-col justify-between rounded-lg border border-border bg-card shadow-xs hover:border-primary transition-[border-color,box-shadow] duration-200">
                  <CardContent className="p-6 flex flex-col h-full">
                    {/* Top Meta Row */}
                    <div className="flex items-center justify-between gap-2 pb-4 border-b border-border/60">
                      <span className="font-mono text-sm font-bold tracking-widest text-primary">
                        STEP {item.step}
                      </span>
                      <span className="text-xs font-mono text-muted-foreground px-2 py-0.5 rounded bg-muted">
                        {item.badge}
                      </span>
                    </div>

                    {/* Icon + Title */}
                    <div className="pt-5 pb-3">
                      <div className="inline-flex items-center justify-center size-10 rounded-md bg-primary/10 text-primary mb-4">
                        <IconComponent className="size-5" strokeWidth={1.75} />
                      </div>
                      <h3 className="text-lg font-bold text-card-foreground font-default">
                        {item.title}
                      </h3>
                    </div>

                    {/* Description & Technical Subtext */}
                    <div className="space-y-2.5 text-sm leading-relaxed text-muted-foreground flex-grow">
                      <p className="text-foreground/90 font-medium">
                        {item.description}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {item.details}
                      </p>
                    </div>

                    {/* Card Footer Step Indicator */}
                    <div className="pt-5 mt-4 border-t border-border/40 flex items-center justify-between text-xs text-muted-foreground">
                      <span className="font-mono">TriServe Field Spec</span>
                      <span className="text-[11px] font-semibold text-primary">Phase {i + 1} of 4</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Operational Guarantee Callout */}
        <div className="mt-12 rounded-lg border border-border bg-muted/60 p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="p-2.5 rounded-md bg-primary text-primary-foreground shrink-0 mt-0.5">
              <ShieldAlert className="size-5" strokeWidth={1.75} />
            </div>
            <div>
              <h4 className="text-base font-bold text-foreground font-default">
                Direct insurance billing and adjuster coordination
              </h4>
              <p className="mt-1 text-sm text-muted-foreground max-w-2xl font-sans">
                We document moisture readings, submit line-item estimates using industry-standard billing software, and communicate with your insurer so you don't face unexpected out-of-pocket delays.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0 w-full md:w-auto">
            <Button asChild className="w-full md:w-auto font-semibold">
              <Link to="/ContactRequestServicePage">
                Request Service
              </Link>
            </Button>
            <Button asChild variant="outline" className="w-full md:w-auto">
              <a href="tel:855-435-6631">
                855-435-6631
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}